/**
 * Fuente única de verdad de versionado del producto.
 * - CHANGELOG_ENTRIES[0] = entrada más reciente.
 * - CURRENT_VERSION se deriva — IMPOSIBLE desincronizar.
 * - bumpVersion() para calcular la próxima versión a agregar.
 *
 * Las entradas DEBEN ser comprensibles para stakeholders no técnicos.
 * Evitá: "endpoint", "JWT", "schema", "BullMQ". Decí: "interfaz",
 * "sesión", "tabla de datos", "cola de trabajo".
 */

export type ChangelogType = 'feature' | 'improvement' | 'fix' | 'security';

export interface ChangelogEntry {
  date: string; // YYYY-MM-DD
  version: string; // X.Y.Z
  type: ChangelogType;
  title: string;
  summary: string;
  details?: string[];
}

export const CHANGELOG_ENTRIES: ChangelogEntry[] = [
  {
    date: '2026-05-28',
    version: '1.9.4',
    type: 'fix',
    title: 'Detección automática de bot WhatsApp colgado (Chromium zombie)',
    summary:
      'Tras el incidente del 28-05 donde el bot dejó de responder 5 horas sin avisar (el navegador interno del gateway se congeló pero la página de estado seguía diciendo "ok"), agregamos dos capas de defensa para que esto nunca más pase desapercibido: el gateway ahora hace un latido cada 60 segundos contra su propio cliente y, si no responde a tiempo, marca la sesión como caída y arranca la reconexión automática. Además el monitor del panel detecta cuando el latido se atrasa más de 5 minutos y muestra una alerta para que alguien actúe sin tener que esperar la queja del cliente.',
    details: [
      'Nuevo heartbeat interno en el gateway WhatsApp: cada 60s ejecuta una llamada de salud al cliente con timeout de 15s. Si responde OK, refresca la marca "última actividad" en la base. Si falla (timeout, error de protocolo), marca la sesión como "fallida" y dispara el reconnect con backoff que ya estaba implementado.',
      'El monitor del bot en el panel ahora detecta el estado "stale": si el gateway dice "listo" pero su última actividad es de hace más de 5 minutos, el banner de salud del bot alerta con instrucciones para reactivar la sesión.',
      'El nuevo estado "stale" se loguea como warn en el API para que también dispare alarmas externas (Uptime Kuma o similares) al primer signo de problema.',
    ],
  },
  {
    date: '2026-05-26',
    version: '1.9.3',
    type: 'improvement',
    title: 'Endurecimiento del flujo de quoted-reply en WhatsApp (post-mortem v1.9.2)',
    summary:
      'Revisión de código del fix de v1.9.2 detectó 8 puntos débiles en el camino de "cliente responde citando un ack viejo y reabrimos el ticket". Aplicados todos en esta versión: cierre atómico del reopen+mensaje+nota en una sola transacción (antes había una ventana donde el mensaje del cliente se perdía si un agente cerraba el ticket en el medio), filtro por grupo en la búsqueda del ticket citado (antes una cita cruzada entre grupos podía rutear mal), SLA tracking para tickets reabiertos, recálculo del deadline de SLA al reabrir, y aviso explícito en WhatsApp cuando un ticket se reabre. También endurecida la validación de payload del gateway para aceptar campos nulos.',
    details: [
      'Reabrir + nota interna + mensaje público + actualización de metadata corren en una sola transacción con bloqueo del ticket. Antes corrían en transacciones separadas y un cierre concurrente podía perder el mensaje del cliente.',
      'Búsqueda del ticket citado ahora exige que el ticket pertenezca al MISMO grupo de WhatsApp donde llega la respuesta. Evita ruteo cruzado si una cita se forwardea entre grupos.',
      'Tickets en estado "reabierto" ahora son monitoreados por el SLA worker (antes quedaban invisibles porque "reabierto" no estaba en la lista de estados activos).',
      'Al reabrir un ticket cerrado, el deadline de SLA se recalcula desde el momento del reopen — antes arrastraba el deadline vencido del ciclo anterior y disparaba "SLA vencido" de inmediato.',
      'Al reabrir, el ticket pasa directo a "abierto" (no se queda en "reabierto"), así un agente lo ve en la cola activa sin necesidad de moverlo manualmente.',
      'WhatsApp ahora envía una confirmación 🔄 al grupo cuando se reabre un ticket por cita — antes el cliente no veía respuesta y solía reescribir.',
      'Mensaje del aportante: si quien responde es el cliente original del ticket, NO se prefija con su propio nombre; si es otro participante del grupo, se prefija para diferenciar en la timeline. Nombres con caracteres especiales se escapan para evitar break del renderizado.',
      'El esquema del webhook acepta valores nulos en mentionsMe/mentionedIds (algunas versiones del gateway los envían así en chats 1-a-1) — antes hacía fallar el webhook entero con 400.',
    ],
  },
  {
    date: '2026-05-26',
    version: '1.9.2',
    type: 'fix',
    title: 'El bot de WhatsApp ya no crea tickets falsos cuando mencionan a otra persona',
    summary:
      'En grupos donde el bot convive con varios participantes, una mención a otro miembro (ej. "@Gerardo el video del drone quedó?") activaba la creación de un ticket porque el bot interpretaba toda mención numérica como dirigida a él. Ahora distingue correctamente "me mencionaron a mí" de "mencionaron a otro" usando la señal real que entrega WhatsApp, y solo abre ticket cuando la mención es al bot. Bonus: si alguien responde citando un mensaje viejo del bot sobre un ticket ya cerrado, el sistema reabre ese ticket automáticamente en lugar de crear uno nuevo.',
    details: [
      'Detección de mención al bot ahora usa el dato directo del gateway de WhatsApp (mentionsMe) en vez de un patrón de texto que confundía menciones a humanos con menciones al bot.',
      'Si el gateway no envía esa señal (por estar desactualizado o roto), el sistema falla cerrado: no crea ticket y deja un aviso en los logs. Mejor un mensaje sin atender (recuperable) que abrir tickets espurios que ensucian el panel y el grupo.',
      'Cuando un cliente cita un mensaje del bot sobre un ticket cerrado y agrega texto nuevo, ese ticket se reabre automáticamente con una nota interna y el mensaje queda en el hilo original — no se crea duplicado.',
      'Se agregaron logs detallados de la decisión de cada mensaje entrante (mention/cita/ignorar) para facilitar diagnóstico futuro.',
    ],
  },
  {
    date: '2026-05-22',
    version: '1.9.1',
    type: 'fix',
    title: 'Limpieza idiomática en los mensajes del bot de WhatsApp',
    summary:
      'Las plantillas que el bot usa para responder en los grupos seguían con expresiones argentinas (mantené, seguís, respondé). Se actualizaron a la forma tuteada estándar (mantén, sigues, responde).',
    details: [
      'Mensaje "Ticket creado": "Mantén esta conversación citando este mensaje" en lugar de "Mantené".',
      'Mensaje "Agente respondió": "Responde citando este mensaje" en lugar de "Respondé".',
      'Mensaje "Ticket cerrado": "Si sigues con dudas" en lugar de "Si seguís con dudas".',
    ],
  },
  {
    date: '2026-05-22',
    version: '1.9.0',
    type: 'feature',
    title: 'Notificaciones en tiempo real cuando entra un ticket nuevo',
    summary:
      'Cuando llega un ticket nuevo (por cualquier canal: WhatsApp, correo, widget, API), el panel ahora avisa de inmediato a todos los agentes conectados. Aparece un toast en la esquina, el contador del menú "Tickets" se actualiza, opcionalmente suena un beep, y opcionalmente aparece una notificación nativa del sistema operativo si la pestaña no está visible. No más enterarse tarde porque nadie estaba mirando.',
    details: [
      'Toast emergente con el código del ticket y un botón "Abrir" para saltar directo al detalle.',
      'Contador rojo al lado de "Tickets" en el menú lateral que muestra cuántos nuevos hay sin ver. Se limpia al entrar a la lista.',
      'Sonido opcional al recibir ticket nuevo. Apagado por defecto, se activa en Configuración → Mi perfil.',
      'Notificación nativa del sistema operativo opcional (Chrome, Edge, Firefox). Apagada por defecto, se activa una sola vez desde Mi perfil — útil cuando tienes el panel en una pestaña en background.',
      'Funciona también con dos pestañas abiertas o múltiples agentes conectados: todos reciben el aviso simultáneamente.',
    ],
  },
  {
    date: '2026-05-22',
    version: '1.8.0',
    type: 'feature',
    title: 'Bloque grande: monitor del bot, filtros nuevos, Categorías y Clientes, Reportes',
    summary:
      'Cinco mejoras que estaban pendientes en el roadmap: (1) alerta visual en el Dashboard cuando el bot de WhatsApp se desconecta o falla, (2) filtros nuevos de prioridad, canal y asignación en la lista de tickets, (3) página completa de Categorías para clasificar tickets con SLAs específicos, (4) página de Clientes con búsqueda, ficha individual e historial de tickets, (5) sección de Reportes con cuatro vistas pintadas (KPIs, volumen, SLA, canales y carga por agente).',
    details: [
      'Banner en el Dashboard si el bot WhatsApp se desconecta o falla — no más enterarse cuando un usuario reclama.',
      'En Tickets, ahora puedes filtrar por prioridad, por canal (correo, WhatsApp, Telegram, widget, API) y por persona asignada (incluyendo "Sin asignar").',
      'Configuración → Categorías permite crear, editar y desactivar categorías. Cada una con SLA propio en minutos (primera respuesta y resolución).',
      'Clientes nueva: lista con búsqueda por nombre/correo/teléfono y ficha individual con todos los tickets que generó cada uno, abierto o cerrado.',
      'Reportes pintados: tarjetas con tickets abiertos, en riesgo, tasa de resolución y tiempo de primera respuesta. Gráfico de volumen últimos 7 días. Distribución por estado. Cumplimiento de SLA con desglose por prioridad. Pastel por canal. Barras de carga por agente.',
    ],
  },
  {
    date: '2026-05-22',
    version: '1.7.4',
    type: 'improvement',
    title: 'Mejoras de WhatsApp: nombres reales, hilos compartidos y localización',
    summary:
      'Tres cambios pedidos a partir del dogfooding del bot: (1) los clientes ya no aparecen con su ID numérico de WhatsApp sino con su nombre real (resuelto contra los contactos del bot), (2) cuando un compañero del equipo cita el mensaje del bot en el grupo, su aporte se suma al ticket abierto en lugar de crear uno nuevo, y (3) eliminamos el lenguaje argentino del panel y la documentación, llevándolo a un español más venezolano/neutro.',
    details: [
      'Identidad real de quien reporta: cuando llega un mensaje al grupo, ahora el sistema consulta al bot por el contacto y guarda el nombre real (o el "pushName" público de WhatsApp), no el ID interno. Los clientes en el panel quedan identificables.',
      'Cuando otra persona del grupo cita el mensaje del bot (responder citando) y hay un ticket abierto del mismo grupo, su aporte se anexa al ticket existente — sin crear un ticket paralelo. Para abrir un caso nuevo basta con mencionar al bot.',
      'Si el aportante es distinto al cliente original del ticket, su nombre aparece como prefijo en el mensaje dentro del panel para que quede claro quién dijo qué.',
      'Limpieza idiomática completa: textos de UI, plantillas, documentación interna y comentarios de código pasan de voseo argentino ("decime", "fíjate", "tenés", "querés") a la forma tuteada estándar usada en Venezuela.',
    ],
  },
  {
    date: '2026-05-22',
    version: '1.7.3',
    type: 'fix',
    title: 'WhatsApp end-to-end completo: respuestas del agente llegan al grupo y privado',
    summary:
      'Después del primer ticket creado desde WhatsApp, descubrimos que las respuestas del agente desde el panel no llegaban al grupo. Tres problemas corregidos: (1) los textos prearmados que el bot usa para responder no se copiaban a la versión publicada, (2) la información del grupo guardada en cada ticket no se podía leer correctamente, (3) el sistema de deploy reiniciaba el bot innecesariamente cada vez. Ahora el flujo está completo: mencionas al bot, se crea ticket, el agente responde desde el panel, y el cliente recibe la respuesta en el grupo o en privado.',
    details: [
      'Las respuestas del agente al grupo y privado ahora se envían correctamente.',
      'El aviso automático de "Ticket TCK-XXX creado" se publica en el grupo apenas el cliente menciona al bot.',
      'El proceso de deploy ya no reinicia el bot de WhatsApp (mantiene la sesión activa sin que tengas que escanear QR de nuevo).',
      'Documentación interna del bot actualizada con todo lo aprendido durante el primer dogfooding (paths reales de API, formato de mensajes, troubleshooting típico).',
    ],
  },
  {
    date: '2026-05-22',
    version: '1.7.2',
    type: 'fix',
    title: 'Correcciones para que los mensajes de WhatsApp lleguen al sistema',
    summary:
      'Cuando alguien mencionaba al bot en un grupo, el mensaje no creaba ticket por dos motivos detectados y corregidos: (1) la verificación de seguridad entre el gateway y el sistema usaba un formato distinto en cada lado y rechazaba todo, (2) el panel "Detectar grupos" no entendía la respuesta del gateway. Ahora ambos flujos funcionan.',
    details: [
      'Las menciones al bot en grupos autorizados ahora crean tickets correctamente.',
      'El botón "Detectar" en Configuración → WhatsApp → Grupos lista los grupos donde el bot ya está como participante, con un click para registrarlos.',
    ],
  },
  {
    date: '2026-05-22',
    version: '1.7.1',
    type: 'fix',
    title: 'Corrección de la pantalla del bot de WhatsApp',
    summary:
      'La pantalla Configuración → WhatsApp → Bot mostraba "Estado: Desconocido" porque el código intentaba leer un campo dos veces (un dato que ya venía desempaquetado por la librería de la API). Corregido — ahora muestra el estado real del bot.',
    details: [
      'Mismo bug detectado y corregido también en el panel "Detectar grupos" dentro de la sección WhatsApp.',
      'Reforzado: las respuestas del API relacionadas al gateway ahora se envían con cabeceras estrictas de "no cachear" para que proxies/CDN intermedios no sirvan respuestas viejas.',
    ],
  },
  {
    date: '2026-05-22',
    version: '1.7.0',
    type: 'feature',
    title: 'Canal WhatsApp para tickets + aviso de versión nueva',
    summary:
      'Tu equipo puede convertir mensajes de grupos de WhatsApp en tickets mencionando al bot — sin sacarlos de WhatsApp ni instalarles otra app. Además, ahora el panel detecta automáticamente cuando sale una versión nueva y te avisa para que recargues, así no quedas trabajando con una versión vieja sin darte cuenta.',
    details: [
      'Nueva sección Configuración → WhatsApp para administrar el bot: estado de la sesión, escaneo del QR, registrar grupos autorizados.',
      'En el detalle de un ticket de canal WhatsApp, puedes elegir responder al grupo (todos lo ven) o privado al cliente.',
      'Landing pública en /operaciones-whatsapp para mostrar la propuesta a posibles clientes.',
      'Cuando sale una versión nueva del panel, aparece un aviso en la esquina inferior derecha con un botón para recargar — para nadie se quede usando una versión vieja después de un deploy.',
      'Mejoras de cache en el servidor para que los redeploys lleguen más rápido a todos los usuarios.',
    ],
  },
  {
    date: '2026-05-20',
    version: '1.6.0',
    type: 'security',
    title: 'Endurecimiento de seguridad y resiliencia operacional',
    summary:
      'Auditoría completa del sistema y aplicación de mitigaciones contra los problemas detectados. Cierra ventanas de robo de sesión, suplantación de correos, ataques por fuerza bruta y caídas silenciosas de procesos internos. El usuario final no nota cambios visibles, pero el sistema es ahora considerablemente más seguro y observable.',
    details: [
      'Cambios de contraseña ahora invalidan TODAS las sesiones activas del usuario (incluidas las de otros dispositivos), evitando que un atacante con tokens viejos siga operando.',
      'Reset de contraseña por administrador también invalida sesiones, cerrando la ventana donde la contraseña vieja seguía activa en otro lugar.',
      'Límites de intentos agregados a las pantallas de renovar sesión y cambiar/resetear contraseña — antes solo el login los tenía.',
      'Validación de correos entrantes contra suplantación: si los chequeos técnicos de origen (SPF/DKIM) fallan ambos, el correo se descarta sin crear ticket.',
      'Si llega un correo "Re: [TCK-XXXX]" desde una dirección distinta a la del cliente del ticket, ya no se mete en el hilo ajeno — se crea un ticket nuevo.',
      'Límite de tamaño en correos entrantes (25 MB) para evitar que adjuntos enormes saturen el sistema.',
      'No se pueden agregar respuestas públicas a tickets cerrados o resueltos sin reabrirlos primero (las notas internas siguen permitidas).',
      'Cambiar la prioridad de un ticket ahora recalcula automáticamente su fecha límite de respuesta (SLA).',
      'Avisos visuales mejorados en la sección Integraciones aclarando que el widget público no requiere API key.',
      'Configuración del servidor: el sistema ahora se rehúsa a arrancar en producción si faltan las claves esenciales (captcha, contraseñas de correo) — fallo ruidoso en vez de silencioso.',
      'Monitor de salud (/healthz) ahora reporta el estado individual de los procesos internos (recepción de correo, envío de notificaciones, vigilancia de SLA) para detectar caídas silenciosas.',
      'Mejoras técnicas internas: caché de verificación de API keys para evitar lentitud bajo carga, transacciones con bloqueo en cambios de tickets para evitar inconsistencias entre instancias.',
    ],
  },
  {
    date: '2026-05-20',
    version: '1.4.0',
    type: 'feature',
    title: 'Gestión de API Keys + Documentación de Integraciones',
    summary:
      'Ya puedes generar y administrar las API Keys desde el panel, y tienes una guía completa con el código exacto para conectar sitios externos al sistema de tickets: widget embebible, llamadas a la API y recepción por correo.',
    details: [
      'Nueva pestaña "API Keys" en Configuración (solo administradores): generar nueva, ver permisos, revocar, eliminar.',
      'Al generar una key, se muestra una sola vez con un ejemplo de uso en curl. Se puede copiar al portapapeles con un click.',
      'Permisos agrupados por caso de uso (integración de cliente / lectura / gestión / avanzado) con descripción de cada uno.',
      'Nueva pestaña "Integraciones" con guía completa para integradores:',
      '  · Generador del snippet del widget — eliges color, plataforma y API key, copias el código y lo pegas en el sitio del cliente.',
      '  · Vista previa visual del botón flotante del widget.',
      '  · Documentación de los endpoints REST principales (crear/listar/leer tickets, agregar mensajes, reportes) con ejemplos curl que ya incluyen tu API key.',
      '  · Recordatorio de la dirección de correo para recibir tickets por email.',
      'Acciones destructivas con confirmación visual diferenciada (revocar = advertencia, eliminar = peligro).',
    ],
  },
  {
    date: '2026-05-20',
    version: '1.3.0',
    type: 'feature',
    title: 'Crear tickets manualmente desde el panel',
    summary:
      'El botón "Nuevo ticket" ahora funciona: abre un formulario para registrar un ticket a mano desde el panel, ideal cuando un cliente llama por teléfono o llega un caso por un canal no automatizado.',
    details: [
      'Formulario con asunto, descripción, prioridad y categoría.',
      'Datos del cliente: nombre (obligatorio), correo y teléfono (opcionales).',
      'Si el cliente ya existe (mismo correo), se reusa su ficha en lugar de duplicarlo.',
      'Validación instantánea de cada campo con mensajes claros.',
      'Al crear, te lleva directo al detalle del ticket nuevo.',
    ],
  },
  {
    date: '2026-05-20',
    version: '1.2.2',
    type: 'improvement',
    title: 'Volver al listado de tickets al cerrar uno',
    summary:
      'Cuando un ticket se marca como Cerrado, el sistema te lleva automáticamente al listado de tickets para que sigas con el próximo de la cola sin tener que volver atrás a mano.',
    details: [
      'El comportamiento aplica solo al estado Cerrado.',
      'Los cambios a otros estados (Resuelto, En proceso, etc.) siguen dejándote en la misma vista del ticket por si necesitas seguir trabajando en él.',
    ],
  },
  {
    date: '2026-05-20',
    version: '1.2.0',
    type: 'feature',
    title: 'Asignación de tickets, confirmaciones y mejor experiencia general',
    summary:
      'Ya se pueden asignar tickets a agentes y supervisores desde el panel. Todos los cambios importantes (estado, prioridad, desactivar usuario) piden confirmación con un diálogo claro. La experiencia visual se moderniza: notificaciones flotantes, validaciones inteligentes en formularios y diálogos accesibles.',
    details: [
      'Nueva sección "Asignación" en el detalle del ticket: ver al agente actual o asignar uno desde un buscador con todos los agentes y supervisores activos.',
      'Cambiar el estado o la prioridad de un ticket ahora pide confirmación, mostrando el cambio exacto antes de aplicarlo (evita clicks accidentales que cierran tickets).',
      'Reemplazo de los avisos del navegador por notificaciones flotantes elegantes y consistentes en toda la aplicación.',
      'Formularios con validación instantánea: los campos resaltan en rojo y muestran el mensaje justo debajo, sin recargas.',
      'Diálogos completamente accesibles: enfoque automático, navegación por teclado, cierre con Escape.',
      'Confirmaciones diferenciadas por nivel de impacto (informativa, atención, peligro) con códigos visuales distintos.',
      'Cuando se desactiva un usuario, se muestra una advertencia clara con el contexto del cambio.',
      'Mejoras técnicas: validaciones con esquemas compartidos, mejor rendimiento en formularios, base sólida para futuras pantallas.',
    ],
  },
  {
    date: '2026-05-20',
    version: '1.1.0',
    type: 'feature',
    title: 'Configuración: Mi perfil y Gestión de Usuarios',
    summary:
      'Nueva sección de Configuración con dos pantallas funcionales: cada usuario puede ver y editar su propio perfil (incluyendo cambiar contraseña), y los administradores pueden invitar, editar y desactivar a otros miembros del equipo.',
    details: [
      'Mi Perfil: ver/editar nombre, ver correo y rol, cambiar contraseña con confirmación.',
      'Gestión de Usuarios (admin): listado del equipo con rol, estado y última sesión.',
      'Crear usuario nuevo con contraseña temporal generada por el sistema, visible una sola vez con botón "copiar" para compartirla por canal seguro.',
      'Editar nombre y rol de cualquier miembro; activar o desactivar accesos.',
      'Botón para generar una nueva contraseña temporal cuando un usuario olvida la suya.',
      'Los supervisores pueden ver el equipo (útil para asignar tickets) pero no modificarlo.',
      'Acceso rápido a "Mi perfil" desde el menú del avatar en la barra superior.',
      'Validaciones: no puedes desactivar tu propia cuenta ni cambiar tu propio rol.',
    ],
  },
  {
    date: '2026-05-20',
    version: '1.0.1',
    type: 'improvement',
    title: 'Vista de tickets más enfocada al abrir el panel',
    summary:
      'Al entrar a la sección de tickets ahora se muestran primero solo los que requieren atención (activos), en lugar del listado completo mezclado con los resueltos y cerrados. El usuario puede cambiar a "Todos" u otro estado en un clic.',
    details: [
      'Nueva opción "Activos" como vista por defecto: incluye Nuevos, Abiertos, En proceso, Esperando cliente y Reabiertos.',
      'Los tickets Resueltos y Cerrados quedan ocultos al inicio, pero accesibles desde los filtros.',
      'El contador superior se ajusta al filtro elegido para evitar confusión.',
    ],
  },
  {
    date: '2026-05-20',
    version: '1.0.0',
    type: 'feature',
    title: 'Lanzamiento del sistema de tickets',
    summary:
      'Plataforma en vivo en soporte.vipsoft.cloud. Todos los canales operativos (API, correo electrónico, widget web), panel administrativo navegable, métricas en tiempo real y vigilancia automática del cumplimiento de tiempos de respuesta.',
    details: [
      'Panel administrativo accesible desde cualquier navegador.',
      'Recepción automática de correos enviados a tickets@vipsoft.cloud.',
      'Widget web embebible para cualquier sitio externo.',
      'API REST documentada para integrar otros sistemas con su propia clave.',
      'Dashboard con métricas reales: tickets abiertos, tiempos de respuesta, distribución por canal.',
      'Detección automática de tickets en riesgo de incumplir tiempo de respuesta (cada minuto).',
      'Cuatro reportes operativos: vista general, cumplimiento de SLA, por canal y productividad por agente.',
      'Conexión segura HTTPS con certificado renovado automáticamente.',
      'Backups diarios con retención de seis meses.',
    ],
  },
  {
    date: '2026-05-20',
    version: '0.8.0',
    type: 'feature',
    title: 'Widget web embebible para sitios externos',
    summary:
      'Cualquier sitio web puede ahora incluir un pequeño bloque de código y empezar a recibir tickets de soporte directamente desde el navegador del visitante. No requiere registro.',
    details: [
      'Bubble flotante en la esquina inferior derecha del sitio anfitrión.',
      'Formulario simple en un modal: nombre, correo, asunto, mensaje.',
      'Aislamiento total del CSS del sitio anfitrión (no rompe el diseño).',
      'Confirmación inmediata con el código del ticket.',
      'Personalización opcional del color de marca.',
      'Protección contra abuso: 30 solicitudes por minuto por dispositivo.',
      'Peso: 3.7 KB comprimidos — no afecta la velocidad del sitio.',
    ],
  },
  {
    date: '2026-05-20',
    version: '0.7.0',
    type: 'feature',
    title: 'Recepción de tickets por correo electrónico',
    summary:
      'Cualquier mensaje a tickets@vipsoft.cloud se convierte automáticamente en un ticket. Las respuestas del cliente se anexan al mismo hilo.',
    details: [
      'Casilla tickets@vipsoft.cloud monitoreada cada 60 segundos.',
      'Las respuestas del cliente al hilo quedan en el ticket original, no se duplica.',
      'Confirmación automática al cliente con el código del ticket.',
      'Notificación al cliente cuando un agente responde desde el panel.',
      'Protección contra duplicados: si el mismo correo llega dos veces, solo se procesa una vez.',
    ],
  },
  {
    date: '2026-05-20',
    version: '0.6.0',
    type: 'feature',
    title: 'Panel administrativo en vivo',
    summary:
      'Los agentes ya pueden ingresar al panel, ver el resumen del día, listar tickets y abrir cada uno para responderlo desde una interfaz dedicada.',
    details: [
      'Tablero con métricas clave: tickets abiertos, en riesgo de tiempo de respuesta, satisfacción promedio.',
      'Lista de tickets con filtros por estado, prioridad, canal y agente.',
      'Detalle de cada ticket con todo el historial de mensajes.',
      'Sección de "Mejoras" accesible desde el menú lateral con badge de la versión actual.',
    ],
  },
  {
    date: '2026-05-20',
    version: '0.5.0',
    type: 'feature',
    title: 'Motor de tickets con API REST',
    summary:
      'Cualquier sistema interno (ERP, e-commerce, app móvil, sitios web) ya puede crear tickets de soporte enviando un mensaje al sistema con su propia clave de integración.',
    details: [
      'Código único TCK-2026-XXXXXX por ticket.',
      'Prioridades urgente/alta/normal/baja.',
      '5 categorías predefinidas con tiempos de respuesta automáticos.',
      'Historial completo: mensajes, cambios de estado, asignaciones.',
      'Claves de integración con permisos granulares.',
      'Auditoría completa: quién, cuándo, IP, qué cambió.',
    ],
  },
  {
    date: '2026-05-20',
    version: '0.3.0',
    type: 'feature',
    title: 'Inicio de sesión seguro para agentes',
    summary:
      'Los agentes ya pueden iniciar sesión con su correo y contraseña. La sesión se renueva sola hasta 7 días.',
    details: [
      'Contraseñas cifradas con el método más seguro disponible.',
      'Bloqueo automático tras 5 intentos fallidos por 15 minutos.',
      'Cada solicitud tiene un código de seguimiento único.',
    ],
  },
  {
    date: '2026-05-20',
    version: '0.2.0',
    type: 'feature',
    title: 'Base de datos y configuración inicial',
    summary:
      'La aplicación ya guarda y consulta información de forma persistente.',
    details: [
      'Conexión segura a la base de datos.',
      'Manejo correcto de tildes, eñes y caracteres del español.',
      'Cuenta de administrador inicial lista.',
    ],
  },
  {
    date: '2026-05-20',
    version: '0.1.0',
    type: 'feature',
    title: 'Bootstrap del proyecto',
    summary:
      'Primera piedra del sistema. Estructura del proyecto y servidor base funcionando.',
    details: [
      'Servidor del sistema en funcionamiento.',
      'Verificación automática de salud.',
      'Pruebas automatizadas en cada cambio.',
    ],
  },
];

export const CURRENT_VERSION = CHANGELOG_ENTRIES[0]?.version ?? '0.0.0';

export function bumpVersion(current: string, type: ChangelogType): string {
  const parts = current.split('.').map(Number);
  const major = parts[0] ?? 0;
  const minor = parts[1] ?? 0;
  const patch = parts[2] ?? 0;
  if (type === 'feature') return `${major}.${minor + 1}.0`;
  return `${major}.${minor}.${patch + 1}`;
}

export const TYPE_META: Record<
  ChangelogType,
  { label: string; badgeClass: string; dotClass: string }
> = {
  feature: {
    label: 'Nueva función',
    badgeClass: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    dotClass: 'bg-emerald-500',
  },
  improvement: {
    label: 'Mejora',
    badgeClass: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    dotClass: 'bg-cyan-500',
  },
  fix: {
    label: 'Corrección',
    badgeClass: 'bg-amber-100 text-amber-700 border-amber-200',
    dotClass: 'bg-amber-500',
  },
  security: {
    label: 'Seguridad',
    badgeClass: 'bg-rose-100 text-rose-700 border-rose-200',
    dotClass: 'bg-rose-500',
  },
};
